import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { ApiError, apiJson, apiVoid } from '@/lib/apiClient';
import type { InterlockType } from '@/features/interlock/useInterlock';
// 18g — un-assign changes what a CUSTOMER can see, so the staff surfaces that
// state it are invalidated from here too. Query keys only, so there is no cycle.
import { commissioningQueryKey } from '@/features/commissioning/useCommissioning';
import { adminLiveQueryKey } from '@/features/live/useAdminLive';
import { adminVehiclesQueryKey } from '@/features/vehicles/useAdminVehicles';

/**
 * The device & SIM registers (slice 18d).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TYPES MIRROR THE WIRE CONTRACT, THEY DO NOT RE-INVENT IT.
 * ─────────────────────────────────────────────────────────────────────────────
 * Every shape below is `AdminDeviceZ` / `AdminSimZ` in
 * `api/src/modules/admin/routes.ts`, read off the zod schema rather than
 * described from the screen's point of view. Same rule `useTenants` and
 * `useProvisioning` follow.
 *
 * 🔴 EVERY WRITE HERE ANSWERS `204 No Content`, SO EVERY WRITE USES `apiVoid`.
 * `apiJson` ends in `res.json()`, which throws `SyntaxError: Unexpected end of
 * JSON input` on an empty body — turning a SUCCESSFUL install into a visible
 * failure. That bug shipped in 17x and was caught while wiring a button, not by
 * a test. The two exceptions are the CREATE routes, which answer `201 {id}`.
 *
 * 🔴 ALL ELEVEN ROUTES ARE `requireSuperAdmin`. An `admin_staff` gets 403 on
 * every one of them, so the nav entries and the screens are super-admin-only —
 * a link that 403s on click is the dangling control this codebase rules out.
 */

export type DeviceStatus = 'in_service' | 'retired' | 'faulty';
export type SimStatus = 'in_service' | 'retired' | 'lost';

export interface AdminDevice {
  id: string;
  imei: string;
  model: string | null;
  firmware: string | null;
  /** NULL = never registered with Traccar. The warehouse state, not an error. */
  traccarDeviceId: number | null;
  status: DeviceStatus;
  notes: string | null;
  /** DERIVED server-side from the absence of an open assignment. No stored flag. */
  inStock: boolean;
  vehicleId: string | null;
  vehiclePlate: string | null;
  simId: string | null;
  simIccid: string | null;
  simMsisdn: string | null;
  simCarrier: string | null;
  /**
   * The engine-interlock kind physically wired to this device, or NULL for "not
   * recorded" (the safe default — the full command guard applies). Mirrors
   * `AdminDeviceZ.interlockType` on the wire. The interlock control lives on THIS
   * register (slice 14e-fix), not the commissioning bench: `devices_commissioned`
   * lists only trackers with no open assignment, so the bench could never reach an
   * INSTALLED device — and an installed device is the only kind whose interlock
   * decides whether a real engine-cut command runs the speed and freshness guards.
   */
  interlockType: InterlockType | null;
}

export interface AdminSim {
  id: string;
  iccid: string;
  /** NULL until the carrier issues a number. Canonical E.164 when set. */
  msisdn: string | null;
  carrier: string;
  apn: string | null;
  activatedAt: string | null;
  status: SimStatus;
  notes: string | null;
  inStock: boolean;
  deviceId: string | null;
  deviceImei: string | null;
}

export interface AssignmentRow {
  id: string;
  fromAt: string;
  toAt: string | null;
  note: string | null;
}

export interface VehicleAssignment extends AssignmentRow {
  vehicleId: string | null;
  vehiclePlate: string | null;
}

export interface SimAssignment extends AssignmentRow {
  simId: string | null;
  simIccid: string | null;
  simMsisdn: string | null;
}

export interface DeviceAssignments {
  vehicles: VehicleAssignment[];
  sims: SimAssignment[];
}

export const devicesQueryKey = ['admin', 'devices'] as const;
export const simsQueryKey = ['admin', 'sims'] as const;
export const deviceAssignmentsQueryKey = (deviceId: string) =>
  ['admin', 'devices', deviceId, 'assignments'] as const;

/** A 4xx is a decision, not a hiccup — retrying it only delays the answer. */
const retry4xxNever = (count: number, error: Error) =>
  !(error instanceof ApiError && error.status >= 400 && error.status < 500) && count < 2;

/**
 * `enabled` is how the screens keep a NON-SUPER-ADMIN from firing a request that
 * can only 403. The redirect already stops the data rendering; this stops the
 * pointless call, so a deep link by an `admin_staff` leaves no 403 in the access
 * log to be investigated later as if it were an attack.
 */
export function useDevices(enabled = true): UseQueryResult<{ devices: AdminDevice[] }> {
  return useQuery({
    queryKey: devicesQueryKey,
    enabled,
    queryFn: ({ signal }) => apiJson<{ devices: AdminDevice[] }>('/api/admin/devices', { signal }),
    retry: retry4xxNever,
  });
}

export function useSims(enabled = true): UseQueryResult<{ sims: AdminSim[] }> {
  return useQuery({
    queryKey: simsQueryKey,
    enabled,
    queryFn: ({ signal }) => apiJson<{ sims: AdminSim[] }>('/api/admin/sims', { signal }),
    retry: retry4xxNever,
  });
}

export function useDeviceAssignments(deviceId: string | null): UseQueryResult<DeviceAssignments> {
  return useQuery({
    queryKey: deviceAssignmentsQueryKey(deviceId ?? ''),
    enabled: deviceId !== null,
    queryFn: ({ signal }) =>
      apiJson<DeviceAssignments>(`/api/admin/devices/${deviceId}/assignments`, { signal }),
    retry: retry4xxNever,
  });
}

/**
 * Both registers are invalidated by every write, on purpose.
 *
 * Installing a SIM changes a row in BOTH lists — the device gains a SIM and the
 * SIM leaves stock — and the two lists are computed from the same assignment
 * rows server-side. Invalidating only the list the operator happens to be
 * looking at would leave the other one claiming a SIM is in stock while it sits
 * in a device: two representations of one fact, disagreeing, which is the exact
 * defect this register exists to remove.
 *
 * The assignment histories hang off `['admin','devices',id,'assignments']`, so
 * invalidating the `['admin','devices']` prefix reaches them too.
 */
function useInventoryMutation<TVars, TRes = void>(
  run: (vars: TVars) => Promise<TRes>,
): UseMutationResult<TRes, Error, TVars> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: devicesQueryKey });
      void qc.invalidateQueries({ queryKey: simsQueryKey });
    },
  });
}

// ── Devices ───────────────────────────────────────────────────────────────────

export interface CreateDeviceBody {
  imei: string;
  model?: string | null;
  firmware?: string | null;
  status?: DeviceStatus;
  notes?: string | null;
}

/**
 * 🔴 NO `traccarDeviceId` FIELD ANYWHERE IN THIS FILE, DELIBERATELY. That id is
 * minted by Traccar at provisioning and stamped by `provisionVehicle`. A human
 * typing one could only be copying it out of Traccar's own UI, and a wrong copy
 * points our row at somebody else's device. The body schema is `.strict()`, so
 * sending one would be a 400 rather than a silently-ignored field.
 */
export function useCreateDevice(): UseMutationResult<{ id: string }, Error, CreateDeviceBody> {
  return useInventoryMutation<CreateDeviceBody, { id: string }>((body) =>
    apiJson<{ id: string }>('/api/admin/devices', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  );
}

export interface PatchDeviceBody {
  model?: string | null;
  firmware?: string | null;
  status?: DeviceStatus;
  notes?: string | null;
}

/**
 * 🔴 NO `imei`. It is the device's identity, and a historical assignment
 * resolves through it — patching it would rewrite what the past says.
 */
export function useUpdateDevice(): UseMutationResult<
  void,
  Error,
  { deviceId: string; body: PatchDeviceBody }
> {
  return useInventoryMutation(({ deviceId, body }) =>
    apiVoid(`/api/admin/devices/${deviceId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  );
}

export interface InstallSimBody {
  simId: string;
  /**
   * ISO-8601. Absent means now — it is the instant of the physical act, so a
   * technician can record this evening what they did this morning.
   */
  at?: string;
  note?: string | null;
}

export function useInstallSim(): UseMutationResult<
  void,
  Error,
  { deviceId: string; body: InstallSimBody }
> {
  return useInventoryMutation(({ deviceId, body }) =>
    apiVoid(`/api/admin/devices/${deviceId}/sim`, { method: 'POST', body: JSON.stringify(body) }),
  );
}

/**
 * 🔴 THIS CLOSES THE OPEN ASSIGNMENT. It deletes nothing. The row stays as the
 * record that the SIM was in this device between two instants, and `to_at` is
 * also the moment it went back to stock.
 */
export function useRemoveSim(): UseMutationResult<
  void,
  Error,
  { deviceId: string; body: { at?: string; note?: string | null } }
> {
  return useInventoryMutation(({ deviceId, body }) =>
    apiVoid(`/api/admin/devices/${deviceId}/sim`, {
      method: 'DELETE',
      body: JSON.stringify(body),
    }),
  );
}

// ── SIMs ──────────────────────────────────────────────────────────────────────

export interface CreateSimBody {
  iccid: string;
  carrier: string;
  apn?: string | null;
  /** Optional — a SIM procured already activated. Normalised to E.164 server-side. */
  msisdn?: string | null;
  status?: SimStatus;
  notes?: string | null;
}

export function useCreateSim(): UseMutationResult<{ id: string }, Error, CreateSimBody> {
  return useInventoryMutation<CreateSimBody, { id: string }>((body) =>
    apiJson<{ id: string }>('/api/admin/sims', { method: 'POST', body: JSON.stringify(body) }),
  );
}

export interface PatchSimBody {
  carrier?: string;
  apn?: string | null;
  status?: SimStatus;
  notes?: string | null;
}

/**
 * 🔴 NO `msisdn` AND NO `iccid`. Both are identity. Issuing a number is
 * `/activate`, which refuses to overwrite one that is already set.
 */
export function useUpdateSim(): UseMutationResult<void, Error, { simId: string; body: PatchSimBody }> {
  return useInventoryMutation(({ simId, body }) =>
    apiVoid(`/api/admin/sims/${simId}`, { method: 'PATCH', body: JSON.stringify(body) }),
  );
}

/** ONE-WAY. A SIM that already carries a number is refused with
 * `sim_already_activated`, never overwritten. */
export function useActivateSim(): UseMutationResult<void, Error, { simId: string; msisdn: string }> {
  return useInventoryMutation(({ simId, msisdn }) =>
    apiVoid(`/api/admin/sims/${simId}/activate`, {
      method: 'POST',
      body: JSON.stringify({ msisdn }),
    }),
  );
}

// ── The vehicle↔device swap, and its 18g sibling ───────────────────────────

/**
 * 🔴 THIS HEADER USED TO SAY "THERE IS NO UNASSIGN, AND THAT IS NOT AN
 * OVERSIGHT". IT IS CORRECTED RATHER THAN LEFT STANDING.
 *
 * The old reasoning was true while `vehicles.traccar_device_id` was NOT NULL: a
 * deviceless vehicle had no honest representation, the migration's trigger
 * refused one, and there was no `DELETE` route to call. Migration 0026 made both
 * projection columns nullable DELIBERATELY, so that state is now real and
 * meaningful — a vehicle marked "No tracker" in the customer's app. Leaving the
 * old sentence here is exactly how the next reader concludes a guard still holds
 * when it does not.
 *
 * What did NOT change: the swap is still atomic, and it still refuses a tracker
 * Traccar has never heard of.
 */
export function useSwapVehicleDevice(): UseMutationResult<
  void,
  Error,
  { vehicleId: string; deviceId: string; at?: string; note?: string | null }
> {
  return useInventoryMutation(({ vehicleId, ...body }) =>
    apiVoid(`/api/admin/vehicles/${vehicleId}/device`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  );
}

// ── Derived predicates, in one place ──────────────────────────────────────────

/**
 * Ready to install in a vehicle — the set the provisioning wizard offers and the
 * swap dialog offers.
 *
 * There is NO `readyToInstall` field on the wire, and there should not be: it is
 * three facts the list already carries, and `provisionVehicle` re-checks all
 * three server-side before it calls Traccar. It lives here once so the wizard
 * and the swap dialog cannot drift apart on what "available" means.
 *
 *   `inStock`               — no open `device_assignments` row.
 *   `status === in_service` — `provisionVehicle` refuses retired and faulty.
 *
 * 🔴 SLICE 18f REMOVED THE THIRD CONDITION, AND LEAVING IT WOULD HAVE BEEN A
 * SILENT DEFECT. It used to require `traccarDeviceId === null`, because the
 * server refused an already-registered device with `device_already_registered`.
 * That code is GONE: `provisionVehicle` now MOVES a commissioned or returned
 * tracker into the tenant's group (the group-move PUT is observed in
 * `traccar-6.14.5-observed-18f.md` B-2). Keeping the condition would have hidden
 * every commissioned tracker from the wizard's picker — which is precisely the
 * hardware Mahad commissions in order to sell — with no error and nothing to
 * see: an empty dropdown that used to be right.
 */
export function isReadyToInstall(d: AdminDevice): boolean {
  return d.inStock && d.status === 'in_service';
}

/**
 * Eligible to be swapped ONTO a vehicle that already has a tracker — and it is
 * deliberately NOT the same set as `isReadyToInstall`.
 *
 * `swapVehicleDevice` refuses `traccar_device_id IS NULL` with
 * `device_has_no_traccar_device`: the vehicle column is NOT NULL, so a device
 * that Traccar has never heard of has nothing honest to project. Provisioning
 * refuses the exact opposite (`device_already_registered`), because it MINTS
 * that id. So the swap candidates are trackers that have been provisioned once
 * and have come back to stock — which today is usually nobody, and the dialog
 * says so plainly rather than showing an empty list with no explanation.
 */
export function isReadyToSwapIn(d: AdminDevice): boolean {
  return d.inStock && d.status === 'in_service' && d.traccarDeviceId !== null;
}

/**
 * May this row be ASSIGNED TO A CUSTOMER — the gate on `/devices`' assign button.
 *
 * 🔴 IT IS NOT "THE SET `/commissioning` LISTS", AND IT USED TO SAY IT WAS.
 * Under its old name `isCommissionedUnassigned` and that docstring, this predicate
 * looked like a client-side copy of the server's `devices_commissioned` view —
 * and disagreed with it, because the view and `listCommissionedDevices` carry NO
 * status filter and both say why: *"A tracker marked faulty on the bench is
 * still commissioned, and hiding it here is how a Traccar device nobody can
 * account for gets forgotten."*
 *
 * That reasoning is about the BENCH LIST, and it is honoured exactly as written:
 * `/commissioning` renders the server's list unfiltered, so a faulty
 * commissioned tracker is still visible and still accounted for. NOTHING on
 * `/devices` filters a list by this predicate — it gates one BUTTON, and
 * "commissioned" and "assignable" are two different facts:
 *
 *   - COMMISSIONED  = in stock, registered with Traccar   (the server's view)
 *   - ASSIGNABLE    = commissioned AND `status === 'in_service'`
 *
 * because `provisionVehicle` REFUSES a device that is retired or faulty
 * (`device_not_in_service`). Offering the action anyway would ship a control
 * whose only possible outcome is a 409. The status is visible on the row and in
 * the "attention" chip, so the absence is explained where the operator is.
 *
 * The name and the docstring are the fix; the condition is unchanged and
 * deliberate. Renaming matters because the OLD name is what made the extra
 * condition look like a divergence from the server rather than a different
 * question.
 */
export function isAssignableToCustomer(d: AdminDevice): boolean {
  return d.inStock && d.status === 'in_service' && d.traccarDeviceId !== null;
}

/** A SIM that can be installed into a device: in stock and not written off. */
export function isSimAvailable(s: AdminSim): boolean {
  return s.inStock && s.status === 'in_service';
}

// ── SLICE 18g — THE WAY BACK ──────────────────────────────────────────

/**
 * The three reasons a tracker comes out of a vehicle. Mirrors `RemovalReason` in
 * `api/src/modules/admin/inventoryStore.ts`, which mirrors the
 * `device_removal_reason` pgEnum. The wire's zod enum is DERIVED from the same
 * record that decides visibility, so a fourth member here that the server does
 * not know is a 400, never a silent acceptance.
 */
export const REMOVAL_REASONS = ['removed', 'moved_to_other_vehicle', 'maintenance'] as const;
export type RemovalReason = (typeof REMOVAL_REASONS)[number];

/**
 * 🔴 WHAT THE CUSTOMER SEES, PER REASON — a client-side MIRROR of the server's
 * `REASON_HIDES_VEHICLE`, and it exists for exactly one job: to state the
 * consequence IN WORDS BEFORE the operator confirms. Mahad, 1 Sep:
 *
 *   removed / moved_to_other_vehicle -> the vehicle is WITHDRAWN
 *   maintenance                      -> the vehicle stays VISIBLE, "No tracker"
 *
 * A total `Record<RemovalReason, boolean>` with no default, for the same reason
 * the server's is: a fourth reason must be a COMPILE ERROR here, not a silent
 * `false` that promises the operator the customer will keep seeing a vehicle
 * which is about to disappear.
 *
 * 🔴 IT DECIDES NOTHING. The server owns the outcome and returns `hidden` on
 * the response; this only chooses which sentence to print beforehand. The dialog
 * renders the server's own `hidden` afterwards, so a divergence between the two
 * copies surfaces to the operator instead of being believed.
 */
export const REASON_HIDES_VEHICLE: Record<RemovalReason, boolean> = {
  removed: true,
  moved_to_other_vehicle: true,
  maintenance: false,
};

export interface UnassignDeviceBody {
  reason: RemovalReason;
  /** ISO-8601. Absent means now — it is the instant of the physical act, so a
   *  technician can record this evening what they did this morning. */
  at?: string;
  note?: string | null;
}

export interface UnassignDeviceResponse {
  vehicleId: string;
  /** The `devices` row uuid the tracker came out of. NOT the IMEI. */
  deviceId: string;
  removedAt: string;
  reason: RemovalReason;
  /** Whether the vehicle was WITHDRAWN as a result of the chosen reason — the
   *  server's own answer, never the client's guess. */
  hidden: boolean;
}

/**
 * 🔴 TAKE THE TRACKER OUT AND LEAVE THE VEHICLE STANDING (18g).
 *
 * This CLOSES the open assignment; it deletes nothing. The closed row stays as
 * the record that this tracker was in this vehicle between two instants, and the
 * vehicle keeps its trips, its history and its detail page — which is the entire
 * point of the `maintenance` case.
 *
 * Invalidates the VEHICLE lists too, not only the registers: for
 * `removed`/`moved_to_other_vehicle` a paying customer stops seeing one of their
 * vehicles, so `/vehicles` and `/live` are stating something that just became
 * false. `commissioningQueryKey` too, because the tracker lands back on the bench.
 */
export function useUnassignVehicleDevice(): UseMutationResult<
  UnassignDeviceResponse,
  Error,
  { vehicleId: string; body: UnassignDeviceBody }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ vehicleId, body }) =>
      apiJson<UnassignDeviceResponse>(`/api/admin/vehicles/${vehicleId}/device`, {
        method: 'DELETE',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: devicesQueryKey });
      void qc.invalidateQueries({ queryKey: simsQueryKey });
      void qc.invalidateQueries({ queryKey: adminVehiclesQueryKey });
      void qc.invalidateQueries({ queryKey: adminLiveQueryKey });
      void qc.invalidateQueries({ queryKey: commissioningQueryKey });
    },
  });
}

export interface DecommissionResponse {
  deviceId: string;
  /** The Traccar id this tracker HELD. Every position it ever sent is still
   *  resolvable through the now-closed `device_traccar_links` row. */
  traccarDeviceId: number;
  decommissionedAt: string;
  /** TRUE when Traccar had already lost the device. The decommission still
   *  completed — said out loud so a retry does not look like a first success. */
  alreadyGone: boolean;
}

/**
 * 🔴 DECOMMISSION — remove the tracker from the tracking engine and KEEP every
 * position it ever sent (18g). The inverse of commissioning, not a delete.
 *
 * A tracker that is still FITTED cannot reach this route: the server refuses with
 * `device_in_use` before it touches Traccar, which is the line that protects the
 * pilot's live vehicle. The button is therefore ABSENT for a fitted device rather
 * than offered and refused — see `canDecommission`.
 */
export function useDecommissionDevice(): UseMutationResult<
  DecommissionResponse,
  Error,
  { deviceId: string; body?: { at?: string; note?: string | null } }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ deviceId, body }) =>
      apiJson<DecommissionResponse>(`/api/admin/devices/${deviceId}/decommission`, {
        method: 'POST',
        body: JSON.stringify(body ?? {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: devicesQueryKey });
      void qc.invalidateQueries({ queryKey: simsQueryKey });
      void qc.invalidateQueries({ queryKey: commissioningQueryKey });
    },
  });
}

/**
 * May this row be DECOMMISSIONED — the gate on `/devices`' decommission button.
 *
 * Commissioned (`traccarDeviceId !== null`) AND not fitted (`inStock`), which are
 * exactly the two preconditions the server checks before it calls Traccar
 * (`not_commissioned`, `device_in_use`). Absent, never disabled: a greyed
 * Decommission on the tracker in a customer's truck teaches nothing, while its
 * absence beside the vehicle plate one column over is the whole explanation.
 *
 * 🔴 NO `status` CONDITION, deliberately — unlike `isAssignableToCustomer`. A
 * FAULTY tracker is precisely one you want out of the tracking engine, and the
 * server has no status check on this route either. A condition the server does
 * not share would hide the action exactly when it is needed.
 */
export function canDecommission(d: AdminDevice): boolean {
  return d.traccarDeviceId !== null && d.inStock;
}
